import { tr, trs } from "@/lib/translations";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang, useSiteSettings } from "@/context/LanguageContext";
import { useWelcomeVoucher } from "@/lib/shared";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Copy, Gift } from "lucide-react";

const SHOWN_KEY = "ph_exit_popup_shown";
const PAGES_KEY = "ph_pages_seen";
const START_KEY = "ph_visit_start";
// Handy/Tablet (kein Hover-Zeiger): kein Sofort-Popup – Google wertet Interstitials direkt nach dem Aufruf als störend
const isTouchDevice = () => window.matchMedia("(hover: none)").matches;
const T = {
  de: { hint: "Gutscheincode beim Buchen eingeben und sparen:", copy: "Code kopieren", copied: "Code kopiert", cta: "Jetzt Ferienobjekt finden", close: "Schließen" },
  en: { hint: "Enter this voucher code when booking and save:", copy: "Copy code", copied: "Code copied", cta: "Find your holiday home", close: "Close" },
  es: { hint: "Introduzca este código al reservar y ahorre:", copy: "Copiar código", copied: "Código copiado", cta: "Buscar alojamiento", close: "Cerrar" },
};
const BLOCKED = /^\/(admin|vermieter|kunde)/;

export const ExitPopup = () => {
  const { lang } = useLang();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const cfg = useSiteSettings();
  const voucher = useWelcomeVoucher();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const n = Number(sessionStorage.getItem(PAGES_KEY) || 0) + 1;
    sessionStorage.setItem(PAGES_KEY, String(n));
    if (!sessionStorage.getItem(START_KEY)) sessionStorage.setItem(START_KEY, String(Date.now()));
  }, [pathname]);

  const blocked = BLOCKED.test(pathname) || search.includes("checkout");
  const enabled = cfg?.exit_popup_enabled && voucher && !blocked;

  useEffect(() => {
    if (!enabled || open) return;
    const last = Number(localStorage.getItem(SHOWN_KEY) || 0);
    if (Date.now() - last < (Number(cfg.exit_popup_days) || 30) * 86400000) return;
    const show = () => { localStorage.setItem(SHOWN_KEY, String(Date.now())); setOpen(true); };
    if (isTouchDevice()) {
      // Handy: erst nach X Sekunden Besuchsdauer (über Seitenwechsel hinweg) oder wenn der Besucher die Seite verlässt (Tab/App-Wechsel)
      const elapsed = Date.now() - Number(sessionStorage.getItem(START_KEY) || Date.now());
      const timer = setTimeout(show, Math.max(1000, (Number(cfg.exit_popup_mobile_seconds) || 30) * 1000 - elapsed));
      const onHide = () => { if (document.visibilityState === "hidden") show(); };
      document.addEventListener("visibilitychange", onHide);
      return () => { clearTimeout(timer); document.removeEventListener("visibilitychange", onHide); };
    }
    if (Number(sessionStorage.getItem(PAGES_KEY) || 0) >= (Number(cfg.exit_popup_pages) || 3)) { show(); return; }
    const timer = setTimeout(show, (Number(cfg.exit_popup_minutes) || 3) * 60000);
    const onLeave = (e) => { if (e.clientY <= 0) show(); };
    document.addEventListener("mouseout", onLeave);
    return () => { clearTimeout(timer); document.removeEventListener("mouseout", onLeave); };
  }, [enabled, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !voucher) return null;
  const t = tr(T, lang);
  const amount = voucher.kind === "percent" ? `${voucher.amount} %` : `${voucher.amount} €`;
  const fill = (s) => (s || "").replace(/\{amount\}/g, amount).replace(/\{code\}/g, voucher.code);
  const title = fill(trs(cfg, "welcome_title", lang));
  const text = fill(trs(cfg, "welcome_text", lang));
  const copy = () => { navigator.clipboard?.writeText(voucher.code); toast.success(t.copied); };
  const go = () => { setOpen(false); navigate("/properties"); };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setOpen(false)} data-testid="exit-popup-overlay">
      <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl p-8 text-center" onClick={(e) => e.stopPropagation()} data-testid="exit-popup">
        <button onClick={() => setOpen(false)} className="absolute top-4 right-4 text-stone-400 hover:text-stone-800" aria-label={t.close} data-testid="exit-popup-close"><X className="w-5 h-5" /></button>
        <div className="mx-auto w-14 h-14 rounded-full bg-tint flex items-center justify-center"><Gift className="w-7 h-7 text-brand" /></div>
        <p className="font-serif text-2xl text-stone-900 mt-4" role="heading" aria-level="3" data-testid="exit-popup-title">{title}</p>
        <div className="text-6xl font-bold text-brand mt-3 tracking-tight" data-testid="exit-popup-amount">{amount}</div>
        <p className="text-sm text-stone-600 mt-3">{text}</p>
        <p className="text-xs text-stone-500 mt-5">{t.hint}</p>
        <button onClick={copy} className="mt-2 inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-signal bg-red-50 px-6 py-3 text-2xl font-mono font-bold text-signal hover:bg-red-100 transition-colors" title={t.copy} data-testid="exit-popup-code">
          {voucher.code} <Copy className="w-4 h-4" />
        </button>
        <button onClick={go} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-white py-3 font-medium transition-colors" data-testid="exit-popup-cta">{t.cta}</button>
      </div>
    </div>
  );
};
